import { useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { Origin, TabsList } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import { GetProjectDetailsResponse } from "../../../../service/query";
import { useAuth } from "../../../../useAuth";
import {
  getProjectAuditHistoryRoute,
  getProjectDetailsRoute,
  getProjectLocationRoute,
  getProjectsRoute,
  getProjectTasksRoute,
  getSettingsAuditHistoryRoute,
  getWorkflowTemplatesRoute,
} from "../../../../utils/routes";
import { ProjectContext } from "../../../shared/projects/project/ProjectContext";

interface UseProjectReturnData {
  projectDetails: GetProjectDetailsResponse | undefined;
  handleBackClick: () => void;
  tabsList: TabsList;
  backButtonText: string;
}

export const useProject = (): UseProjectReturnData => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const { currentUserType } = useAuth();
  const { projectDetails, isExternalProject } = useContext(ProjectContext);

  const origin = searchParams.get("origin");

  let backButtonText = stateOrigin?.goBackText;

  if (!backButtonText) {
    switch (origin) {
      case Origin.ProjectAuditPage:
      case Origin.OrganisationAuditPage:
        backButtonText = "Back to audit";
        break;
      case Origin.WorkflowsPage:
        backButtonText = "Back to workflows";
        break;
      default:
        backButtonText = "Back to projects";
    }
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin.from);
      return;
    }

    switch (origin) {
      case Origin.ProjectAuditPage:
        if (projectDetails?.uuid) {
          navigate(getProjectAuditHistoryRoute(projectDetails.uuid, currentUserType));
        }
        break;
      case Origin.OrganisationAuditPage:
        navigate(getSettingsAuditHistoryRoute(currentUserType));
        return;
      case Origin.WorkflowsPage:
        navigate(getWorkflowTemplatesRoute(currentUserType));
        return;
      default:
        navigate(getProjectsRoute(currentUserType));
    }
  };

  const getTabsList = (): TabsList => {
    const list = [
      {
        label: "Overview",
        route: `/d/projects/${projectDetails?.uuid}/overview${origin ? `?origin=${origin}` : ""}`,
      },
      {
        label: "Details",
        route: getProjectDetailsRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Location",
        route: getProjectLocationRoute(
          projectDetails?.uuid || "",
          OrganisationTypeConstants.DEVELOPER,
          origin
            ? {
                origin,
              }
            : undefined
        ),
      },
      {
        label: "Inventory",
        route: `/d/projects/${projectDetails?.uuid}/inventory${origin ? `?origin=${origin}` : ""}`,
      },
      {
        label: "Activities",
        route: `/d/projects/${projectDetails?.uuid}/activities${origin ? `?origin=${origin}` : ""}`,
      },
      {
        label: "Marketing settings",
        route: `/d/projects/${projectDetails?.uuid}/marketing-settings${origin ? `?origin=${origin}` : ""}`,
      },
      {
        label: "Documents",
        route: `/d/projects/${projectDetails?.uuid}/documents${origin ? `?origin=${origin}` : ""}`,
      },
    ];

    if (!isExternalProject()) {
      list.push({
        label: "Access",
        route: `/d/projects/${projectDetails?.uuid}/access${origin ? `?origin=${origin}` : ""}`,
      });
    }

    list.push({
      label: "Tasks",
      route: getProjectTasksRoute(projectDetails?.uuid || "", OrganisationTypeConstants.DEVELOPER),
    });

    return list;
  };

  return {
    projectDetails,
    handleBackClick,
    tabsList: getTabsList(),
    backButtonText,
  };
};
